import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from '@hooks/useToast';
import { useAppContext } from '@contexts/app';
import { useCreateWebPush } from '@services/webPushNotifications/create';
import { UserWebPushResp } from '@services/webPushNotifications/types';
import { queryKeys } from '@utils/consts';
import { isPermissionBlocked, isPushSupported, subscribeToPush } from './helpers';
import { PushBannerStatus } from './types';

export const usePushSubscription = () => {
  const { t } = useTranslation('app');
  const { setData } = useAppContext();
  const queryClient = useQueryClient();
  const { mutateAsync: create } = useCreateWebPush();
  const [status, setStatus] = useState<PushBannerStatus>(() =>
    isPermissionBlocked() ? 'blocked' : 'idle',
  );

  useEffect(() => {
    if (!isPushSupported()) return;
    let cancelled = false;
    let permissionStatus: PermissionStatus | undefined;

    const sync = () => {
      const blocked = isPermissionBlocked();
      setStatus((prev) => {
        if (blocked) return 'blocked';
        return prev === 'blocked' ? 'idle' : prev;
      });
    };

    if ('permissions' in navigator) {
      navigator.permissions
        .query({ name: 'notifications' })
        .then((result) => {
          if (cancelled) return;
          permissionStatus = result;
          result.addEventListener('change', sync);
        })
        .catch(() => undefined);
    }
    document.addEventListener('visibilitychange', sync);

    return () => {
      cancelled = true;
      permissionStatus?.removeEventListener('change', sync);
      document.removeEventListener('visibilitychange', sync);
    };
  }, []);

  const enable = useCallback(async () => {
    setStatus('working');
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        setStatus(permission === 'denied' ? 'blocked' : 'idle');
        return;
      }
      const webpushNotifications = await subscribeToPush();
      const resp = await create({ webpushNotifications });
      if (!resp?.success || !resp.pushEnabled) throw new Error('Push subscription was not saved');

      queryClient.setQueryData<UserWebPushResp>(queryKeys.webpushNotifications, {
        pushEnabled: true,
      });
      setData?.((prev) => ({ ...prev, pushEnabled: true }));
      setStatus('idle');
      toast({ variant: 'default', title: t('navigation.push.confirmed') });
    } catch (error) {
      console.error(error);
      setStatus(isPermissionBlocked() ? 'blocked' : 'failed');
    }
  }, [create, queryClient, setData, t]);

  return { status, enable };
};
